import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getActiveCampaign } from "@/lib/api";

const DISMISS_KEY = "launchBannerDismissedId";

function useCountdown(endAt) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!endAt) return null;
  const diff = new Date(endAt).getTime() - Date.now();
  if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, done: true, tick };
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { d, h, m, s, done: false, tick };
}

export default function LaunchBanner() {
  const { lang } = useLanguage();
  const [campaign, setCampaign] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const time = useCountdown(campaign?.end_at);

  useEffect(() => {
    getActiveCampaign()
      .then((c) => {
        if (c) {
          const last = localStorage.getItem(DISMISS_KEY);
          if (last === c.id) setDismissed(true);
          setCampaign(c);
        }
      })
      .catch(() => {});
  }, []);

  if (!campaign || dismissed || time?.done) return null;

  const message = lang === "en" ? campaign.message_en : campaign.message_no;
  const title = lang === "en" ? campaign.title_en : campaign.title_no;
  const cta = lang === "en" ? "Shop now" : "Handle nå";

  const close = () => {
    localStorage.setItem(DISMISS_KEY, campaign.id);
    setDismissed(true);
  };

  const TimeBox = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="bg-white/20 backdrop-blur-sm rounded-lg px-2 py-1 min-w-[34px] text-center">
        <span className="font-heading text-base md:text-lg font-semibold text-white tabular-nums">
          {String(value).padStart(2, "0")}
        </span>
      </div>
      <span className="text-[9px] uppercase tracking-widest text-white/70 mt-0.5">
        {label}
      </span>
    </div>
  );

  return (
    <div
      data-testid="launch-banner"
      className="relative w-full bg-gradient-to-r from-[#D97B66] via-[#C66A55] to-[#D97B66] text-white overflow-hidden"
    >
      {/* Decorative sparkle background */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <Sparkles className="absolute top-2 left-[10%] w-4 h-4" />
        <Sparkles className="absolute bottom-2 left-[40%] w-3 h-3" />
        <Sparkles className="absolute top-3 right-[15%] w-5 h-5" />
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-10 py-3 flex items-center justify-between gap-3 flex-wrap relative">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Sparkles className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
          <div className="min-w-0">
            <p className="font-heading text-sm md:text-base font-medium leading-tight">
              <span className="font-semibold">{title}</span>
              <span className="hidden sm:inline"> — </span>
              <span className="block sm:inline text-white/95">{message}</span>
            </p>
            <p className="text-[11px] text-white/85 mt-0.5">
              {lang === "en" ? "Use code" : "Bruk koden"}{" "}
              <span className="bg-white/25 rounded px-1.5 py-0.5 font-mono font-semibold tracking-wider">
                {campaign.discount_code}
              </span>
            </p>
          </div>
        </div>

        {time && (
          <div className="hidden md:flex items-center gap-2" data-testid="launch-countdown">
            <TimeBox value={time.d} label={lang === "en" ? "days" : "dager"} />
            <span className="text-white/40">:</span>
            <TimeBox value={time.h} label={lang === "en" ? "hrs" : "tim"} />
            <span className="text-white/40">:</span>
            <TimeBox value={time.m} label={lang === "en" ? "min" : "min"} />
            <span className="text-white/40">:</span>
            <TimeBox value={time.s} label={lang === "en" ? "sec" : "sek"} />
          </div>
        )}

        <Link
          to={`/butikk?code=${campaign.discount_code}`}
          data-testid="launch-banner-cta"
          className="flex items-center gap-1.5 bg-white/95 hover:bg-white text-[#2C302E] rounded-full px-3 md:px-4 py-1.5 text-xs md:text-sm font-semibold whitespace-nowrap transition-all"
        >
          {cta}
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.2} />
        </Link>

        <button
          onClick={close}
          data-testid="launch-banner-close"
          aria-label="Lukk"
          className="text-white/80 hover:text-white p-1"
        >
          <X className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}
